// ─── Chat key gate — the wiring point between identity state and the UI ──────
// See CHAT_E2E_DESIGN.md §3/§8 and context/ChatIdentityContext.js.
//
// Usage (by the Chat sidebar panel, #148):
//
//   <ChatKeyGate>
//     <ChatPanel />
//   </ChatKeyGate>
//
// `children` always render underneath — per the design doc, a locked thread
// view should still be visible with messages rendered as a
// "🔒 Unlock chat to view" placeholder, not hidden entirely. This component
// only adds the blocking first-run/unlock modal on top when needed; it does
// not gate whether `children` mounts at all. Anything that needs the actual
// key material (compose/send, decrypt-on-render — #149) should pull it from
// `useChatIdentity()` directly and treat a non-'ready' status as "can't
// decrypt yet," not as "this component shouldn't render."

import React from 'react';
import { ChatIdentityProvider, useChatIdentity } from '../../context/ChatIdentityContext';
import SetupChatModal from './SetupChatModal';
import UnlockChatModal from './UnlockChatModal';

function ChatKeyGateInner({ children, onClose }) {
  const { status, completeSetup, completeUnlock } = useChatIdentity();

  return (
    <>
      {children}
      {/* onDismiss lets the user back out to the rest of the CRM without
          completing setup/unlock — previously these modals had no close
          control at all, which left users stuck if they didn't have their
          passphrase handy right that moment. Dismissing just closes the chat
          drawer (same as the drawer's own close button); it doesn't change
          chatKeyStatus, so the same modal reappears next time chat is
          opened until the user actually completes one of the flows. */}
      {status === 'needs-setup' && <SetupChatModal onComplete={completeSetup} onDismiss={onClose} />}
      {/* onForgotKey reuses completeSetup (not a separate function) — its two
          writes (setChatKey/setChatKeyBackup) are merge-writes, so calling it
          again here safely overwrites whatever's stuck server-side (e.g. an
          orphaned public key from a setup that got interrupted before the
          backup write completed) and gives the user a fresh keypair under a
          passphrase of their choosing, exactly like first-run setup. */}
      {status === 'needs-unlock' && <UnlockChatModal onComplete={completeUnlock} onForgotKey={completeSetup} onDismiss={onClose} />}
    </>
  );
}

export default function ChatKeyGate({ children, onClose }) {
  return (
    <ChatIdentityProvider>
      <ChatKeyGateInner onClose={onClose}>{children}</ChatKeyGateInner>
    </ChatIdentityProvider>
  );
}
